import React, { useEffect, useState } from 'react';
import { Button, Card, Descriptions, Spin, Tabs, Tag, Typography } from 'antd';
import { EditOutlined } from '@ant-design/icons';
import { useNavigate, useParams } from 'react-router-dom';
import { useEmployees } from '../../hooks/useEmployees';
import { useLeave } from '../../hooks/useLeave';

const { Title } = Typography;

const statusColor: Record<string, string> = {
  ACTIVE: 'green', INACTIVE: 'default', ON_LEAVE: 'orange', TERMINATED: 'red',
};

const EmployeeDetailView: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { activeEmployee, fetchEmployee } = useEmployees();
  const { leaveBalances, fetchLeaveBalances } = useLeave();
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (id) {
      setLoading(true);
      const year = new Date().getFullYear();
      fetchEmployee(Number(id))
        .then(() => {
          fetchLeaveBalances(Number(id), year);
        })
        .finally(() => setLoading(false));
    }
  }, [id]);

  if (loading || !activeEmployee) return <Spin className="p-6" />;

  const emp = activeEmployee;

  const items = [
    {
      key: 'info',
      label: 'Personal Info',
      children: (
        <Descriptions bordered column={2} size="small">
          <Descriptions.Item label="Employee No.">{emp.employeeNumber}</Descriptions.Item>
          <Descriptions.Item label="Status">
            <Tag color={statusColor[emp.status]}>{emp.status}</Tag>
          </Descriptions.Item>
          <Descriptions.Item label="Full Name">{emp.firstName} {emp.middleName ?? ''} {emp.lastName}</Descriptions.Item>
          <Descriptions.Item label="Email">{emp.email ?? '—'}</Descriptions.Item>
          <Descriptions.Item label="Phone">{emp.phoneNumber ?? '—'}</Descriptions.Item>
          <Descriptions.Item label="National ID">{emp.nationalId ?? '—'}</Descriptions.Item>
          <Descriptions.Item label="Address" span={2}>{emp.address ?? '—'}</Descriptions.Item>
          <Descriptions.Item label="Emergency Contact">{emp.emergencyContactName ?? '—'}</Descriptions.Item>
          <Descriptions.Item label="Emergency Phone">{emp.emergencyContactPhone ?? '—'}</Descriptions.Item>
        </Descriptions>
      ),
    },
    {
      key: 'employment',
      label: 'Employment',
      children: (
        <Descriptions bordered column={2} size="small">
          <Descriptions.Item label="Department">{emp.departmentName ?? '—'}</Descriptions.Item>
          <Descriptions.Item label="Position">{emp.positionTitle ?? '—'}</Descriptions.Item>
          <Descriptions.Item label="Employment Type">{emp.employmentType}</Descriptions.Item>
          <Descriptions.Item label="Hire Date">{emp.hireDate}</Descriptions.Item>
          <Descriptions.Item label="Supervisor">{emp.supervisorName ?? '—'}</Descriptions.Item>
          <Descriptions.Item label="Termination Date">{emp.terminationDate ?? '—'}</Descriptions.Item>
        </Descriptions>
      ),
    },
    {
      key: 'leave',
      label: 'Leave Balances',
      children: (
        <table className="full-width" style={{ borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              {['Leave Type', 'Entitled', 'Carried Over', 'Taken', 'Pending', 'Remaining'].map((h) => (
                <th key={h} style={{ textAlign: 'left', padding: '8px', borderBottom: '1px solid #f0f0f0' }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {leaveBalances.map((b) => (
              <tr key={b.id}>
                <td style={{ padding: '8px' }}>{b.leaveTypeName}</td>
                <td style={{ padding: '8px' }}>{b.entitledDays}</td>
                <td style={{ padding: '8px' }}>{b.carriedOverDays}</td>
                <td style={{ padding: '8px' }}>{b.takenDays}</td>
                <td style={{ padding: '8px' }}>{b.pendingDays}</td>
                <td style={{ padding: '8px' }}>
                  <Tag color={b.remainingDays > 0 ? 'green' : 'red'}>{b.remainingDays}</Tag>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      ),
    },
  ];

  return (
    <div className="p-6">
      <Card
        title={
          <Title level={4} style={{ margin: 0 }}>
            {emp.firstName} {emp.lastName}
            <Tag color={statusColor[emp.status]} style={{ marginLeft: 12, verticalAlign: 'middle' }}>
              {emp.status}
            </Tag>
          </Title>
        }
        extra={
          <Button
            type="primary"
            icon={<EditOutlined />}
            onClick={() => navigate(`/hr/employees/${id}/edit`)}
          >
            Edit
          </Button>
        }
      >
        <Tabs items={items} />
      </Card>
    </div>
  );
};

export default EmployeeDetailView;
